import type {
	AddressResponse,
	MappedPackage,
	Zone
} from '@/types/components/signup'
import type { KioskContact } from '@/types/pages/kiosk'
import {
	mapLabelToStatus,
	mapZoneToStatus
} from '@/utils/fiberStatusMap'
import {
	StorageSerializers,
	useStorage
} from '@vueuse/core'

export function useAddressLocalStorage() {
	const defaultValue: AddressResponse = {
		address: '',
		addressId: 0,
		status: 'internetUnavailable', // Default status
		billables: [],
		accountFound: false
	}

	// Create reactive localStorage with object serialization
	const cachedAddressResponse = useStorage('addressResponse', defaultValue, undefined, {
		serializer: StorageSerializers.object
	})

	const setAddressObject = (address: string, addressId: number, zone: Zone, billables: MappedPackage[], accountFound: boolean, planPhaseLabel?: string) => {
		const status = planPhaseLabel ? mapLabelToStatus(planPhaseLabel, !!billables?.length) : mapZoneToStatus(zone)

		// Create clean data (no circular references)
		// Use structuredClone if available, otherwise fall back to JSON method
		let cleanBillables: MappedPackage[]
		try {
			cleanBillables = structuredClone ? structuredClone(billables) : JSON.parse(JSON.stringify(billables))
		} catch {
			// Fallback if structuredClone fails or billables has circular refs
			cleanBillables = JSON.parse(JSON.stringify(billables))
		}

		const addressData = {
			address,
			addressId,
			status,
			billables: cleanBillables,
			accountFound
		}

		// Update the reactive storage instead of manual localStorage
		cachedAddressResponse.value = addressData

		return addressData
	}

	return {
		setAddressObject,
		cachedAddressResponse
	}
}

export function useLanguageLocalStorage() {
	const setLanguage = (language: string) => {
		localStorage.setItem('language', language)
	}
	const cachedLanguage = useStorage('language', null)

	return {
		setLanguage,
		cachedLanguage
	}
}

export function useKioskLocalStorage() {
	const setContact = (contact: KioskContact) => {
		if (localStorage.getItem('kioskContact')) {
			const currentContacts = JSON.parse(localStorage.getItem('kioskContact') as string)
			const newContact = [ ...currentContacts, contact ]
			localStorage.setItem('kioskContact', JSON.stringify(newContact))
		} else {
			localStorage.setItem('kioskContact', JSON.stringify([ contact ]))
		}
	}

	const getContacts = () => {
		return JSON.parse(localStorage?.getItem('kioskContact') as string) || []
	}

	const clearContacts = () => {
		localStorage.removeItem('kioskContact')
	}
	return {
		setContact,
		getContacts,
		clearContacts
	}
}
